import type { ChatMessage, DeltaType } from '../types';

/**
 * Ordering authority for the activity cluster of an agent turn.
 *
 * Activity rows (voice line, tool cards, system notes, navigation intents,
 * content-update pills, object-update stacks) render before the assistant
 * text anchor and sort by a composite logical key:
 *   (round, phase, call_index, server seq, arrival order)
 * Server-stamped `seq` comes from the single ordered turn channel; rows
 * without metadata keep arrival order (stable sort).
 */
export const ACTIVITY_PHASE_RANK: Partial<Record<DeltaType, number>> = {
  voice: 0,
  tool: 1,
  system: 1,
};

/** Rows that belong to the pre-anchor activity cluster for a run. */
export const isActivityMessage = (m: ChatMessage, runId: string): boolean => {
  if (m.runId !== runId) return false;
  if (m.isDelta) return m.deltaType !== 'memory';
  return m.messageType === 'navigation_intent' || m.messageType === 'object_update';
};

export const compareActivity = (a: ChatMessage, b: ChatMessage): number => {
  const MAX = Number.MAX_SAFE_INTEGER;
  const roundA = a.toolRound ?? MAX;
  const roundB = b.toolRound ?? MAX;
  if (roundA !== roundB) return roundA - roundB;
  const phaseA = a.isDelta ? ACTIVITY_PHASE_RANK[a.deltaType] ?? 2 : 2;
  const phaseB = b.isDelta ? ACTIVITY_PHASE_RANK[b.deltaType] ?? 2 : 2;
  if (phaseA !== phaseB) return phaseA - phaseB;
  const callA = a.toolCallIndex ?? MAX;
  const callB = b.toolCallIndex ?? MAX;
  if (callA !== callB) return callA - callB;
  const seqA = a.orderSeq ?? MAX;
  const seqB = b.orderSeq ?? MAX;
  return seqA - seqB; // equal keys: Array.sort is stable, arrival order preserved
};

/** Run id for the in-flight turn — first runId on messages after the latest user message. */
export function activeStreamingRunId(
  messages: ChatMessage[],
  isStreaming: boolean,
): string | null {
  if (!isStreaming) return null;
  let lastUserIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].isUser) {
      lastUserIdx = i;
      break;
    }
  }
  if (lastUserIdx < 0) return null;
  for (let i = messages.length - 1; i > lastUserIdx; i--) {
    const runId = messages[i].runId;
    if (runId) return runId;
  }
  return null;
}

/** Latest voice status line for a run (used for shimmer targeting). */
export function latestVoiceMessageIdForRun(
  messages: ChatMessage[],
  runId: string | null,
): string | null {
  if (!runId) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (
      !m.isUser &&
      m.isDelta &&
      m.deltaType === 'voice' &&
      m.content?.trim() &&
      m.runId === runId
    ) {
      return m.id;
    }
  }
  return null;
}
